import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { ExclamationCircleIcon } from '@heroicons/react/outline'

import { fetchTarifZonesStatus } from '../../../../services/shippingCostsBulkService'
import {
  isSpecialZone,
  TARIF_ZONE_NA,
  TARIF_ZONE_EXTENDED,
  COST_SHADOWED_BY_DISABLED_TIER,
  MISSING_XDOCK_ROUTE,
} from '../../../CubboLastmile/ShippingMethods/ShippingCosts/costHelpers'
import { FieldLabel } from './formPrimitives'

const M = 'lastmile_management.coverage_manager.update_modal'

const INPUT_CLS = 'block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'

const Helper = ({ children }) => <div className="mt-1 text-xs text-gray-400">{children}</div>

/**
 * Why a zone cannot receive coverages, or null when it can. The rules are
 * ShippingCoverages::ZoneUpdateValidator's, read off the tarif-zones-status payload the
 * costs tab already consumes, so the option the operator cannot pick and the 422 they
 * would have got name the same problem.
 *
 * Returns the backend's own error code, so one translation map serves both the disabled
 * option and the rejection banner.
 *
 * One gap stays server-side: MISSING_XDOCK_ROUTE is only computed for zones that already
 * have coverage, so a brand-new Cubbo zone can still bounce. That is why the banner
 * translates the same codes.
 */
export const zoneUpdateBlocker = zoneRow => {
  if (!zoneRow) return null
  const flags = [...(zoneRow.missing || []), ...(zoneRow.notices || [])]

  // Step for step in ZoneUpdateValidator#validate_zone!'s order, which matters:
  //   1. NONE_APPLIES returns early — it skips BOTH the cost and the xdock check.
  //   2. every other zone, EXTENDED_ZONE included, must price.
  //   3. EXTENDED_ZONE then returns: it resolves through a bare find_by, so it can
  //      neither shadow nor need an xdock. (The service never emits the shadow flag for
  //      it either, so the order is belt and braces — but a mirror that diverges from
  //      its original is how the two drift apart.)
  //   4. the shadow check, then the Cubbo-only xdock checks.
  if (zoneRow.zone === TARIF_ZONE_NA) return null
  if (!zoneRow.has_cost) return 'ZONE_HAS_NO_SHIPPING_COST'
  if (isSpecialZone(zoneRow.zone)) return null
  if (flags.includes(COST_SHADOWED_BY_DISABLED_TIER)) return 'ZONE_COST_SHADOWED_BY_DISABLED_TIER'
  if (zoneRow.is_cubbo && !zoneRow.has_xdock_zone) return 'ZONE_HAS_NO_XDOCK'
  if (zoneRow.is_cubbo && flags.includes(MISSING_XDOCK_ROUTE)) return 'ZONE_HAS_NO_XDOCK_ROUTE'
  return null
}

/**
 * Whether the method can take the ODA/OPA surcharge at all, or the code explaining why
 * not. ZoneUpdateValidator#validate_extended_zone! is a BARE `exists?` on an
 * EXTENDED_ZONE cost row — no cost>0, no coverage_enabled — so the mirror here is
 * "the zone carries any tier", not `has_cost`.
 *
 * An empty list means the zone set was never fetched (the method-wide form does not
 * fetch it) — unknown, not missing, so nothing is blocked and the 422 still guards.
 */
export const extendedZoneBlocker = zones => {
  if (!zones || zones.length === 0) return null
  const extended = zones.find(z => z.zone === TARIF_ZONE_EXTENDED)
  return extended && (extended.tiers || []).length > 0 ? null : 'MISSING_EXTENDED_ZONE_COST'
}

/**
 * The method's zone universe (zones in use ∪ zones with a price), each already judged
 * against the guard. `isLoading` keeps the select disabled rather than showing an empty
 * list that reads as "this method has no zones".
 */
export const useMethodZones = shippingMethodId => {
  const { data, isLoading, error } = useQuery(
    // Same key the costs tab uses (TarifZonesStatusTab), so opening this modal right
    // after looking at Zonas is a cache hit — the underlying query walks every coverage
    // row of the method to build the zone universe, which is not cheap on a BR method.
    ['tarif_zones_status', shippingMethodId, '', false],
    () => fetchTarifZonesStatus(shippingMethodId),
    { enabled: Boolean(shippingMethodId), staleTime: 60_000, refetchOnWindowFocus: false },
  )

  const zones = useMemo(() => (data?.zones || []).map(z => ({ ...z, blocker: zoneUpdateBlocker(z) })), [data])

  return { zones, isLoading, error }
}

/**
 * Target zone for a reclassification. The empty value means "leave it alone": the caller
 * omits `zone` from the payload entirely, so an SLA-only edit never re-validates (and
 * never gets refused by) the zone the coverages are already on.
 */
export const ZoneSelect = ({ zones, isLoading, error, value, onChange, label, help }) => {
  const { t } = useTranslation()
  // A failed fetch and a method with no zones both arrive as an empty list, and telling
  // the operator "this method has no zones yet" when the request simply failed sends
  // them to reconfigure zones that already exist. Keep the two apart.
  const hint = isLoading
    ? t(`${M}.fields.zone_loading`)
    : error
      ? t(`${M}.fields.zone_load_failed`)
      : zones.length === 0
        ? t(`${M}.fields.zone_empty`)
        : help || t(`${M}.fields.zone_help`)
  return (
    <div>
      <FieldLabel>{label || t(`${M}.fields.zone`)}</FieldLabel>
      <select value={value} onChange={e => onChange(e.target.value)} disabled={isLoading} className={INPUT_CLS}>
        <option value="">{t(`${M}.fields.zone_keep`)}</option>
        {zones.map(z => (
          <option key={z.zone} value={z.zone} disabled={Boolean(z.blocker)}>
            {z.blocker ? `${z.zone} — ${t(`${M}.server_codes.${z.blocker}`)}` : z.zone}
          </option>
        ))}
      </select>
      <Helper>{hint}</Helper>
    </div>
  )
}

/**
 * The ODA/OPA surcharge flag — a DIFFERENT column from `zone`, even though ops calls
 * both "extendida". Tri-state on purpose: "" keeps each coverage's current flag, so a
 * bulk edit does not silently clear the surcharge on the CPs that already carry it.
 */
export const ExtendedZoneSelect = ({ zones, value, onChange, label }) => {
  const { t } = useTranslation()
  // Only "Sí" is gated: turning the surcharge OFF is always allowed, and is exactly what
  // an operator does on a method whose EXTENDED_ZONE tariff was deleted.
  const blocker = extendedZoneBlocker(zones)
  return (
    <div>
      <FieldLabel>{label || t(`${M}.fields.extended_zone`)}</FieldLabel>
      <select value={value} onChange={e => onChange(e.target.value)} className={INPUT_CLS}>
        <option value="">{t(`${M}.fields.extended_zone_keep`)}</option>
        <option value="true" disabled={Boolean(blocker)}>
          {blocker ? `${t(`${M}.fields.extended_zone_yes`)} — ${t(`${M}.server_codes.${blocker}`)}` : t(`${M}.fields.extended_zone_yes`)}
        </option>
        <option value="false">{t(`${M}.fields.extended_zone_no`)}</option>
      </select>
      <Helper>{t(`${M}.fields.extended_zone_help`)}</Helper>
    </div>
  )
}

/**
 * A reclassification reaches further than the operator picked: the workers run
 * `update_all` over the whole range (or the whole selection) with `with_disabled`, so a
 * coverage that is paused today is moved too and nothing records which ones those were.
 * The range tab shows a CP count, which is the size of the write but not its shape — the
 * CSV preview already says this in its own words, and the two surfaces do the same write.
 *
 * Range only. On a selection the operator hand-picked rows whose status is on screen, and
 * that form already states its own blast radius; a second amber box there is noise.
 */
export const ZoneRewriteWarning = ({ zone, extendedZone, count }) => {
  const { t } = useTranslation()
  if (zone === '' && extendedZone === '') return null
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
      <span className="text-sm leading-5 text-amber-800">{t(`${M}.zone_rewrite_range_warning`, { count })}</span>
    </div>
  )
}

/** Amber note for the one target zone the backend accepts but warns about. */
export const NoneAppliesWarning = ({ zone }) => {
  const { t } = useTranslation()
  if (zone !== TARIF_ZONE_NA) return null
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
      <span className="text-sm leading-5 text-amber-800">{t(`${M}.zone_none_applies_warning`)}</span>
    </div>
  )
}

/**
 * A coverage-update rejection, split in two: the translated meaning of `error_code` and
 * the server's own message, which names the zone and the weight bands at fault. Ops
 * relays that detail to engineering, so it is shown rather than swallowed.
 */
export const coverageUpdateError = (t, e) => {
  // ONLY the response body. Reading `e.message` too would surface axios's untranslated
  // "Network Error" where the previous code fell back to the localized copy — the
  // request never landed, so there is no server sentence to relay.
  const body = e?.response?.data || {}
  const detail = body.error || body.message || null
  const code = body.error_code
  if (code) {
    const key = `${M}.server_codes.${code}`
    const translated = t(key)
    // i18next echoes the key back when it has no entry — fall through to the raw
    // message rather than printing a dotted path at the operator.
    if (translated !== key) return { title: translated, detail }
  }
  // No code, or a code with no entry yet: lead with the server's own sentence and keep
  // the code underneath. An UNKNOWN code is precisely the one ops needs to quote, so it
  // must never be the thing that gets dropped.
  return { title: detail || t(`${M}.error`), detail: code || null }
}

export const UpdateErrorBanner = ({ error }) => {
  if (!error) return null
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <div>{error.title}</div>
      {error.detail && error.detail !== error.title && <div className="mt-1 text-xs text-red-700">{error.detail}</div>}
    </div>
  )
}
